import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Patterns, Input } from '@treezor/design-system';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';

import { fetchGlobalSupportPublicKey, setGlobalSupportPublicKey } from '@/store/actions/encryption.actions';
import { IGlobalSupportUserKeys } from '@/store/models/encryption.model';
import { DashboardHelpers } from '@/utils/DashboardHelpers';
import { PairData } from '@/ui-kit/PairData';
import { Loader } from '@/ui-kit/loader/Loader';
import { Page } from '@/ui-kit/templates/Page';

export const Cryptography = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchGlobalSupportPublicKey());
  }, [dispatch]);

  const globalSupportPairKey = useAppSelector((state) => state.encryption.globalSupportPairKey);
  const isLoading = useAppSelector((state) => state.encryption.isLoading);
  const error = useAppSelector((state) => state.encryption.error);

  const { watch, handleSubmit, register } = useForm<IGlobalSupportUserKeys>({
    defaultValues: { globalSupportPublicKey: '' },
  });

  const keyWatch = watch('globalSupportPublicKey');

  const submitGlobalSupportPublicKey = (globalSupportUserKeys) => {
    dispatch(setGlobalSupportPublicKey(globalSupportUserKeys));
  };

  const informations = useMemo<{ [key: string]: string } | undefined>(() => {
    if (!globalSupportPairKey) return undefined;

    return { [t('pages.administration.crypto.rules.informations')]: globalSupportPairKey.publicKey };
  }, [globalSupportPairKey, t]);

  if (error) return <div className="text-error text-center m-11"> {DashboardHelpers.getErrorMessage(error)} </div>;

  return (
    <Page title={t('pages.administration.crypto.header')}>
      {isLoading && <Loader />}

      {!isLoading && !globalSupportPairKey && (
        <form className="flex flex-col gap-5 p-5 max-w-[480px]" onSubmit={handleSubmit(submitGlobalSupportPublicKey)}>
          <Input
            type="text"
            required
            label={t('pages.administration.crypto.forms.globalSupportPublicKey')}
            {...register('globalSupportPublicKey', { pattern: Patterns.cryptoKey })}
            error={
              keyWatch && !Patterns.cryptoKey.test(keyWatch)
                ? t('pages.administration.crypto.rules.message')
                : undefined
            }
          />

          <Button
            id="cryptography-page-btn"
            className="w-fit"
            type="submit"
            disabled={!keyWatch || !Patterns.cryptoKey.test(keyWatch)}
          >
            {t('pages.administration.crypto.forms.btnSubmit')}
          </Button>
        </form>
      )}

      {!isLoading && globalSupportPairKey && informations && (
        <PairData
          data={informations}
          linkingText={<span className="typo-body-semibold-small text-secondary">&nbsp;{' : '}&nbsp;</span>}
        />
      )}
    </Page>
  );
};
